import { UUID } from 'crypto';
import { createBerthCallOrderChange } from 'src/factory/Stowage/StowagePlanChanges/StowageChangeData/BerthCallChangeDataFactory';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { deepCopy } from 'src/utils/objectUtils';

export const onBerthCallDragStart = (
    event: React.DragEvent<HTMLDivElement>,
    rotationBerthCall: RotationBerthCall,
    dragStartBerthCall: React.MutableRefObject<RotationBerthCall>
) => {
    const target = event.target as HTMLDivElement;
    dragStartBerthCall.current = rotationBerthCall;
    setTimeout(() => target.classList.add('dragging'), 0);
};

export const onBerthCallDragOver = (() => {
    let previousGuid;
    return (
        event: React.DragEvent<HTMLDivElement>,
        rotationBerthCall: RotationBerthCall,
        dragStartBerthCall: React.MutableRefObject<RotationBerthCall>,
        draggoverElement: React.MutableRefObject<HTMLDivElement>,
        dragoverBerthCall: React.MutableRefObject<RotationBerthCall>
    ) => {
        event.stopPropagation();
        event.preventDefault();
        if (previousGuid === rotationBerthCall.berthCallGuid) {
            return;
        }
        const target = event.target as HTMLDivElement;
        const targetParent = target.closest('.dragging-parent-berth') as HTMLDivElement;
        if (rotationBerthCall.portCallGuid === dragStartBerthCall.current?.portCallGuid) {
            if (targetParent) {
                setTimeout(() => {
                    if (draggoverElement.current) {
                        draggoverElement.current.classList.remove('dragging-over');
                    }

                    targetParent.classList.add('dragging-over');
                    draggoverElement.current = targetParent;
                    dragoverBerthCall.current = rotationBerthCall;
                }, 0);
            }
        }
        previousGuid = rotationBerthCall.berthCallGuid;
    };
})();

export const onDragoverBerthCallParent = (
    event: React.DragEvent<HTMLLIElement>,
    berthCallItem: RotationBerthCall,
    dragStartBerthCall: React.MutableRefObject<RotationBerthCall>
) => {
    event.preventDefault();
    event.stopPropagation();
    if (isDraggingOverInvalidBerthCall(dragStartBerthCall.current, berthCallItem)) {
        event.dataTransfer.dropEffect = 'none';
    } else {
        event.dataTransfer.dropEffect = 'move';
    }
};

export const onBerthCallDragEnd = (
    event: React.DragEvent<HTMLDivElement>,
    rotationBerthCall: RotationBerthCall,
    stowagePlanGuid: UUID,
    dragStartBerthCall: React.MutableRefObject<RotationBerthCall>,
    draggoverElement: React.MutableRefObject<HTMLDivElement>,
    dragoverBerthCall: React.MutableRefObject<RotationBerthCall>
) => {
    const stowagePlanChanges: StowagePlanChange[] = [];
    // TODO: NAMING-CONVENTION - Variable name starts with underscore (_dragStartBerthCall) which is typically reserved for private/internal variables. Should use standard naming convention.
    const _dragStartBerthCall = dragStartBerthCall.current;
    if (draggoverElement.current) {
        draggoverElement.current.classList.remove('dragging-over');
        draggoverElement.current = null;
    }
    if (dragStartBerthCall.current) {
        dragStartBerthCall.current = null;
    }

    if (dragoverBerthCall.current) {
        // Check if trying to drop on the same berth call
        const isSameBerthCall = dragoverBerthCall.current.berthCallGuid === rotationBerthCall.berthCallGuid;
        if (isDraggingOverInvalidBerthCall(_dragStartBerthCall, dragoverBerthCall.current) || isSameBerthCall) {
            // Skip processing if dropping onto itself
            dragoverBerthCall.current = null;
            const target = event.target as HTMLDivElement;
            target.classList.remove('dragging');
            return stowagePlanChanges; // Return empty array to prevent changes
        } // TODO: NAMING-CONVENTION - Variable names start with underscores (_originalBerthCalls, _berthCalls) which is typically reserved for private/internal variables. Should use standard naming convention.
        const _originalBerthCalls = deepCopy(rotationBerthCall.portCall.berthCalls);
        const _berthCalls = [...rotationBerthCall.portCall.berthCalls];
        const indexOfMoveTo = _berthCalls.findIndex((bertCall) => bertCall.berthCallGuid === dragoverBerthCall.current.berthCallGuid);
        const indexOfMoveFrom = _berthCalls.findIndex((bertCall) => bertCall.berthCallGuid === rotationBerthCall.berthCallGuid);

        const swapBerthCalls = (i, j) => {
            const current = _berthCalls[i];
            const next = _berthCalls[j];

            [_berthCalls[i].berthSequenceNumber, _berthCalls[j].berthSequenceNumber] = [next.berthSequenceNumber, current.berthSequenceNumber];
            [_berthCalls[i], _berthCalls[j]] = [next, current];
        };

        const step = indexOfMoveFrom > indexOfMoveTo ? -1 : 1;

        for (let i = indexOfMoveFrom; i !== indexOfMoveTo; i += step) {
            swapBerthCalls(i, i + step);
        }

        for (let i = indexOfMoveFrom; i !== indexOfMoveTo + step; i += step) {
            stowagePlanChanges.push(
                createBerthCallOrderChange(
                    stowagePlanGuid,
                    _berthCalls[i].berthCallGuid,
                    _berthCalls[i].berthSequenceNumber,
                    _originalBerthCalls.find((bc) => bc.berthCallGuid === _berthCalls[i].berthCallGuid)
                )
            );
        }

        dragoverBerthCall.current = null;
    }

    const target = event.target as HTMLDivElement;
    target.classList.remove('dragging');

    return stowagePlanChanges;
};

const isDraggingOverInvalidBerthCall = (dragStartBerthCall: RotationBerthCall, berthCallItem: RotationBerthCall) => {
    const isSameBerthCall = dragStartBerthCall?.berthCallGuid === berthCallItem.berthCallGuid;
    const isDifferentPortCall = berthCallItem.portCallGuid !== dragStartBerthCall?.portCallGuid;

    return isSameBerthCall || isDifferentPortCall;
};
